import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import SearchIcon from "../../../Components/Icon-SVG/SearchIcon";
import IconXMarkIcon from "../../../Components/Icon-SVG/IconXMarkIcon";
import {
  DetailActions,
  DetailField,
  DetailGrid,
  DetailItem,
  DetailNote,
  DetailPanel,
  DetailSection,
} from "../../../Components/common/DetailPanel/DetailPanel";
import useClipboard from "../../../utils/hook/useClipboard";
import { ClearIocSearch, RequestIocSearch } from "../../../store/actions/analysisReportActions";
import { IOC_TYPES, formatDate, windowLabel } from "./iocUtils";

const MIN_QUERY_LENGTH = 2;
const SEARCH_DELAY = 400;

// What "related" holds for each kind of indicator
const RELATED_LABELS = {
  url: "Server IPs",
  domain: "Resolved IPs",
  ip: "Hostnames",
  hash: "File names",
};

const TYPE_FILTERS = [{ id: "", label: "All" }, ...IOC_TYPES];

const reportPath = (reportId) => `/analysis-reports?data=${reportId}`;

const TypePill = ({ type }) => {
  const info = IOC_TYPES.find((item) => item.id === type);
  return <span className={`ioc-type ioc-type--${type}`}>{info?.short || type}</span>;
};

const CountChips = ({ counts }) => (
  <span className="ioc-search__chips">
    {IOC_TYPES.filter((type) => counts?.[type.id]).map((type) => (
      <span key={type.id} className={`ioc-chip ioc-chip--${type.id}`}>
        {counts[type.id]} {type.short}
      </span>
    ))}
  </span>
);

const SampleRow = ({ sample }) => (
  <DetailItem
    actions={
      <Link className="ioc-search__report-link" to={reportPath(sample.reportId)}>
        Open report
      </Link>
    }
  >
    <div className="ioc-sample">
      <span className="ioc-sample__name" title={sample.fileName}>
        {sample.fileName || "Unnamed sample"}
      </span>
      <span className="ioc-sample__meta">
        <span className="ioc-sample__hash" title={sample.sha256}>
          {sample.sha256}
        </span>
        <span>{formatDate(sample.date)}</span>
      </span>
      {sample.counts && <CountChips counts={sample.counts} />}
    </div>
  </DetailItem>
);

const ResultDetail = ({ result, onSearch }) => {
  const copy = useClipboard();
  const relatedLabel = RELATED_LABELS[result.type];

  return (
    <DetailPanel className="ioc-result__detail">
      <DetailGrid columns={4}>
        <DetailField label="First seen">{formatDate(result.firstSeen)}</DetailField>
        <DetailField label="Last seen">{formatDate(result.lastSeen)}</DetailField>
        <DetailField label="Malicious reports">{result.reportCount.toLocaleString()}</DetailField>
        <DetailField label="Seen in">{result.sources.join(", ")}</DetailField>
        {result.countries.length > 0 && (
          <DetailField label="Countries" wide>
            {result.countries
              .map((country) => (country.code ? `${country.name} (${country.code})` : country.name))
              .join(", ")}
          </DetailField>
        )}
        {result.related.length > 0 && (
          <DetailField label={relatedLabel} wide>
            <span className="ioc-search__related">
              {result.related.map((item) =>
                result.type === "hash" ? (
                  <span key={item} className="ioc-related">
                    {item}
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    className="ioc-related ioc-related--action"
                    title={`Search ${item}`}
                    onClick={() => onSearch(item)}
                  >
                    {item}
                  </button>
                ),
              )}
              {result.relatedTotal > result.related.length && (
                <span className="ioc-search__more">
                  +{result.relatedTotal - result.related.length} more
                </span>
              )}
            </span>
          </DetailField>
        )}
      </DetailGrid>
      <DetailSection title="Malicious samples" count={result.reportCount}>
        {result.samples.map((sample) => (
          <SampleRow key={sample.reportId} sample={sample} />
        ))}
        {result.reportCount > result.samples.length && (
          <p className="ioc-search__more">Showing the newest {result.samples.length}.</p>
        )}
      </DetailSection>
      <DetailActions>
        <button
          type="button"
          className="ioc-search__action"
          onClick={() => copy(result.value, "Indicator copied", "Copying failed")}
        >
          Copy indicator
        </button>
      </DetailActions>
    </DetailPanel>
  );
};

const ResultRow = ({ result, open, onToggle, onSearch }) => (
  <li className={`ioc-result ${open ? "ioc-result--open" : ""}`}>
    <button type="button" className="ioc-result__row" onClick={onToggle} aria-expanded={open}>
      <TypePill type={result.type} />
      <span className="ioc-result__value" title={result.value}>
        {result.value}
        {result.match === "exact" && <span className="ioc-result__exact">Exact match</span>}
      </span>
      <span className="ioc-result__stat">
        <b>{result.reportCount.toLocaleString()}</b> report{result.reportCount === 1 ? "" : "s"}
      </span>
      <span className="ioc-result__stat ioc-result__country">
        {result.countries[0]?.name || "-"}
      </span>
      <span className="ioc-result__stat">{formatDate(result.lastSeen)}</span>
      <span className="ioc-result__chevron" aria-hidden="true" />
    </button>
    {open && <ResultDetail result={result} onSearch={onSearch} />}
  </li>
);

/**
 * Text search over the indicators of the selected window. The query can be
 * set from outside (a click on a dashboard list) through `preset`.
 */
const IocSearch = ({ filterByLast, preset }) => {
  const dispatch = useDispatch();
  const search = useSelector((state) => state.analysisReports.iocSearch);
  const loading = useSelector((state) => state.analysisReports.iocSearchLoading);
  const error = useSelector((state) => state.analysisReports.iocSearchError);

  const inputRef = useRef(null);
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [type, setType] = useState("");
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    if (!preset?.value) return;
    setInput(preset.value);
    setQuery(preset.value);
    setType("");
    inputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [preset]);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(input.trim()), SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [input]);

  useEffect(() => {
    setOpenKey(null);
    if (query.length < MIN_QUERY_LENGTH) {
      dispatch(ClearIocSearch());
      return;
    }
    if (filterByLast) dispatch(RequestIocSearch({ query, type, filterByLast }));
  }, [dispatch, query, type, filterByLast]);

  useEffect(() => () => dispatch(ClearIocSearch()), [dispatch]);

  const searchFor = (value) => {
    setInput(value);
    setQuery(value);
    setType("");
  };

  const active = query.length >= MIN_QUERY_LENGTH;
  const results = search?.results || [];
  const samples = search?.samples || [];
  const typeCounts = search?.typeCounts || {};
  const allCount = Object.values(typeCounts).reduce((sum, count) => sum + count, 0);
  const nothingFound = search && !results.length && !samples.length && !search.olderSample;

  return (
    <section className="ioc-search mgw-dashboard__item mgw-dashboard__item--span-12">
      <header className="mgw-dashboard__item__header">
        <div>
          <h2>Search Indicators</h2>
          <p className="mgw-dashboard__item__subtitle">
            URLs, domains, IP addresses and file hashes from malicious reports of the{" "}
            {windowLabel(filterByLast)}
          </p>
        </div>
      </header>

      <form
        className="ioc-search__box border"
        onSubmit={(e) => {
          e.preventDefault();
          setQuery(input.trim());
        }}
      >
        <SearchIcon width={16} height={16} />
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search a domain, URL, IP address, SHA-256 / MD5 hash or file name"
          aria-label="Search indicators"
          spellCheck={false}
        />
        {input && (
          <button
            type="button"
            className="ioc-search__clear"
            onClick={() => searchFor("")}
            title="Clear search"
          >
            <IconXMarkIcon />
          </button>
        )}
      </form>

      <div className="ioc-search__toolbar">
        <div className="ioc-search__filters" role="tablist" aria-label="Indicator type">
          {TYPE_FILTERS.map((filter) => {
            const count = filter.id ? typeCounts[filter.id] : allCount;
            return (
              <button
                key={filter.id || "all"}
                type="button"
                role="tab"
                aria-selected={type === filter.id}
                className={`ioc-search__filter ${type === filter.id ? "ioc-search__filter--active" : ""}`}
                onClick={() => setType(filter.id)}
              >
                {filter.label}
                {active && search && count !== undefined && <span>{count.toLocaleString()}</span>}
              </button>
            );
          })}
        </div>
        <p className="ioc-search__status">
          {!active
            ? "Partial values work; defanged indicators like evil[.]com are accepted."
            : loading
              ? "Searching..."
              : search
                ? `${search.total.toLocaleString()} indicator${search.total === 1 ? "" : "s"} match`
                : ""}
        </p>
      </div>

      {active && error && <DetailNote tone="danger">{error}</DetailNote>}

      {active && search && (
        <div className={`ioc-search__results ${loading ? "ioc-search__results--loading" : ""}`}>
          {search.truncated && (
            <DetailNote tone="muted">
              Searched the newest {search.reportsScanned.toLocaleString()} of{" "}
              {search.reportsTotal.toLocaleString()} malicious reports in this period.
            </DetailNote>
          )}

          {search.olderSample && (
            <DetailNote tone="warning" title="Seen before this period">
              <Link to={reportPath(search.olderSample.reportId)}>
                {search.olderSample.fileName || "A sample"}
              </Link>{" "}
              with this SHA-256 was found malicious on {formatDate(search.olderSample.date)}.
            </DetailNote>
          )}

          {samples.length > 0 && (
            <DetailSection title="Matching samples" count={search.samplesTotal}>
              {samples.map((sample) => (
                <SampleRow key={sample.reportId} sample={sample} />
              ))}
            </DetailSection>
          )}

          {results.length > 0 && (
            <DetailSection title="Indicators" count={search.total}>
              <div className="ioc-result__head" aria-hidden="true">
                <span>Type</span>
                <span>Indicator</span>
                <span>Seen in</span>
                <span>Country</span>
                <span>Last seen</span>
                <span />
              </div>
              <ul className="ioc-search__list">
                {results.map((result) => {
                  const key = `${result.type}:${result.value}`;
                  return (
                    <ResultRow
                      key={key}
                      result={result}
                      open={openKey === key}
                      onToggle={() => setOpenKey(openKey === key ? null : key)}
                      onSearch={searchFor}
                    />
                  );
                })}
              </ul>
              {search.total > results.length && (
                <p className="ioc-search__more">
                  Showing the best {results.length} of {search.total.toLocaleString()} matches. Type
                  more of the indicator to narrow them down.
                </p>
              )}
            </DetailSection>
          )}

          {nothingFound && (
            <p className="mgw-dashboard__no-data">
              Nothing matches "{search.query}" in the {windowLabel(filterByLast)}.
            </p>
          )}
        </div>
      )}
    </section>
  );
};

export default IocSearch;
